import { useCallback, useState } from 'react';

import { getCredentials } from '@/services/amazon/credentials';
import type { AmazonCredential } from '@/services/amazon/credentials.d';
import { getApiUsageStats } from '@/services/amazon/monitoring';
import type { ApiUsageStats } from '@/services/amazon/monitoring.d';
import { getOrders, getSyncStatus } from '@/services/amazon/orders';
import type { AmazonOrder } from '@/services/amazon/orders.d';
import type { SyncStatusInfo } from '@/services/amazon/sync.d';
import { extractItems, extractPagination } from '@/utils/apiAdapter';
import { debugLog } from '@/utils/debug';

/**
 * Amazon SP-API 状态管理模型
 */

// Amazon状态管理接口已移除，直接使用具体的状态变量

export default function useAmazonModel() {
  // ============= 状态定义 =============

  const [credentials, setCredentials] = useState<AmazonCredential[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [activeCredential, setActiveCredential] = useState<AmazonCredential>();

  const [syncStatus, setSyncStatus] = useState<SyncStatusInfo | null>(null);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);

  const [apiUsage, setApiUsage] = useState<ApiUsageStats | null>(null);
  const [apiUsageLoading, setApiUsageLoading] = useState(false);

  const [orders, setOrders] = useState<AmazonOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersTotal, setOrdersTotal] = useState(0);

  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>();

  // ============= 凭据管理 =============

  /**
   * 获取凭据列表
   */
  const fetchCredentials = useCallback(
    async (params?: any) => {
      setCredentialsLoading(true);
      try {
        const response = await getCredentials(params);
        if (response.code === 200) {
          // 使用统一适配器提取凭据数组
          const credentialsList = extractItems(response);
          setCredentials(credentialsList);

          // 设置默认活跃凭据（第一个正常状态的凭据）
          if (!activeCredential && credentialsList.length > 0) {
            const active =
              credentialsList.find((c) => c.status === 'active') ||
              credentialsList[0];
            setActiveCredential(active);
          }
        }
        return response;
      } catch (error) {
        debugLog.error('获取凭据数据失败:', error);
        throw error;
      } finally {
        setCredentialsLoading(false);
      }
    },
    [activeCredential],
  );

  /**
   * 添加凭据到状态
   */
  const addCredential = useCallback(
    (credential: AmazonCredential) => {
      setCredentials((prev) => [...prev, credential]);

      // 如果是第一个凭据，设为活跃凭据
      if (!activeCredential) {
        setActiveCredential(credential);
      }
    },
    [activeCredential],
  );

  /**
   * 更新凭据状态
   */
  const updateCredential = useCallback(
    (credentialId: number, updates: Partial<AmazonCredential>) => {
      setCredentials((prev) =>
        prev.map((credential) =>
          credential.id === credentialId
            ? { ...credential, ...updates }
            : credential,
        ),
      );

      // 如果更新的是活跃凭据，也更新活跃凭据状态
      if (activeCredential?.id === credentialId) {
        setActiveCredential((prev) => (prev ? { ...prev, ...updates } : prev));
      }
    },
    [activeCredential],
  );

  /**
   * 删除凭据
   */
  const removeCredential = useCallback(
    (credentialId: number) => {
      setCredentials((prev) =>
        prev.filter((credential) => credential.id !== credentialId),
      );

      // 如果删除的是活跃凭据，清除活跃凭据或选择新的
      if (activeCredential?.id === credentialId) {
        setActiveCredential((_prev) => {
          const remaining = credentials.filter((c) => c.id !== credentialId);
          return remaining.length > 0 ? remaining[0] : undefined;
        });
      }
    },
    [activeCredential, credentials],
  );

  /**
   * 切换活跃凭据
   */
  const switchActiveCredential = useCallback((credential: AmazonCredential) => {
    setActiveCredential(credential);
  }, []);

  // ============= 同步状态管理 =============

  /**
   * 获取同步状态
   */
  const fetchSyncStatus = useCallback(async () => {
    setSyncStatusLoading(true);
    try {
      const response = await getSyncStatus();
      if (response.code === 200) {
        setSyncStatus(response.data);
      }
      return response;
    } catch (error) {
      debugLog.error('获取同步状态失败:', error);
      throw error;
    } finally {
      setSyncStatusLoading(false);
    }
  }, []);

  /**
   * 更新同步状态
   */
  const updateSyncStatus = useCallback((status: Partial<SyncStatusInfo>) => {
    setSyncStatus((prev) => (prev ? { ...prev, ...status } : null));
  }, []);

  // ============= API使用统计 =============

  /**
   * 获取API使用统计
   */
  const fetchApiUsage = useCallback(async () => {
    setApiUsageLoading(true);
    try {
      const response = await getApiUsageStats();
      if (response.success) {
        setApiUsage(response.data);
      }
      return response;
    } catch (error) {
      debugLog.error('获取API使用统计失败:', error);
      throw error;
    } finally {
      setApiUsageLoading(false);
    }
  }, []);

  // ============= 订单管理 =============

  /**
   * 获取订单列表
   */
  const fetchOrders = useCallback(async (params?: any) => {
    setOrdersLoading(true);
    try {
      const response = await getOrders(params);
      if (response.success) {
        // 使用统一适配器提取订单数据
        const ordersList = extractItems(response);
        const pagination = extractPagination(response);
        const total = pagination?.total || ordersList.length;

        setOrders(ordersList);
        setOrdersTotal(total);
      }
      return response;
    } catch (error) {
      debugLog.error('获取订单列表失败:', error);
      throw error;
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  /**
   * 添加订单到状态
   */
  const addOrder = useCallback((order: AmazonOrder) => {
    setOrders((prev) => [order, ...prev]);
    setOrdersTotal((prev) => prev + 1);
  }, []);

  /**
   * 更新订单状态
   */
  const updateOrder = useCallback(
    (orderId: number, updates: Partial<AmazonOrder>) => {
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, ...updates } : order,
        ),
      );
    },
    [],
  );

  // ============= 全局操作 =============

  /**
   * 刷新所有数据
   */
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchCredentials(),
        fetchSyncStatus(),
        fetchApiUsage(),
        fetchOrders(),
      ]);
      setLastRefreshTime(new Date());
    } catch (error) {
      debugLog.error('刷新数据失败:', error);
      throw error;
    } finally {
      setRefreshing(false);
    }
  }, [fetchCredentials, fetchSyncStatus, fetchApiUsage, fetchOrders]);

  /**
   * 清除所有状态
   */
  const clearAll = useCallback(() => {
    setCredentials([]);
    setActiveCredential(undefined);
    setSyncStatus(null);
    setApiUsage(null);
    setOrders([]);
    setOrdersTotal(0);
    setLastRefreshTime(undefined);
  }, []);

  // ============= 计算属性 =============

  /**
   * 获取健康凭据数量
   */
  const healthyCredentialsCount = credentials.filter(
    (c) => c.status === 'active',
  ).length;

  /**
   * 获取活跃同步任务数量
   */
  const activeSyncJobs = syncStatus?.current_jobs || 0;

  /**
   * 检查是否有警告
   */
  const hasWarnings = credentials.some(
    (c) => c.health_check.warnings.length > 0 || c.sync_error_count > 0,
  );

  /**
   * 获取今日API使用率
   */
  const todayApiUsageRate = apiUsage
    ? (apiUsage.today.requests / 1000) * 100
    : 0; // 假设日限制1000次

  // ============= 返回状态和方法 =============

  return {
    // 状态
    credentials,
    credentialsLoading,
    activeCredential,
    syncStatus,
    syncStatusLoading,
    apiUsage,
    apiUsageLoading,
    orders,
    ordersLoading,
    ordersTotal,
    refreshing,
    lastRefreshTime,

    // 凭据操作
    fetchCredentials,
    addCredential,
    updateCredential,
    removeCredential,
    switchActiveCredential,

    // 同步操作
    fetchSyncStatus,
    updateSyncStatus,

    // API统计
    fetchApiUsage,

    // 订单操作
    fetchOrders,
    addOrder,
    updateOrder,

    // 全局操作
    refreshAll,
    clearAll,

    // 计算属性
    healthyCredentialsCount,
    activeSyncJobs,
    hasWarnings,
    todayApiUsageRate,
  };
}
